import React, { useState, useEffect } from 'react';
import { Plus, X, Search, Lock, Globe, Trash2, Edit, BookOpen, Clock, Users } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';

const CadernoScreen = ({ currentUser }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [viewingNote, setViewingNote] = useState(null);
    
    // Form state
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [visibility, setVisibility] = useState('public');

    const [users, setUsers] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [allowedSectors, setAllowedSectors] = useState([]);

    useEffect(() => {
        fetchNotes();
        fetchUsersAndSectors();
    }, []);

    const fetchUsersAndSectors = async () => {
        try {
            const [uRes, sRes] = await Promise.all([
                api.get('/users'),
                api.get('/sectors')
            ]);
            setUsers(Array.isArray(uRes.data) ? uRes.data : []);
            setSectors(Array.isArray(sRes.data) ? sRes.data : []);
        } catch (e) {
            console.error('Erro ao buscar usuarios e setores:', e);
        }
    };

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const res = await api.get('/notes');
            setNotes(res.data || []);
        } catch (e) {
            console.error('Erro ao buscar notas:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!title.trim() || !content.trim() || content === '<p><br></p>') {
            alert('Título e conteúdo são obrigatórios.');
            return;
        }

        try {
            const payload = { 
                title, 
                content, 
                visibility,
                allowedUsers: visibility === 'custom' ? allowedUsers : [],
                allowedSectors: visibility === 'custom' ? allowedSectors : []
            };

            if (editingId) {
                await api.put(`/notes/${editingId}`, payload);
            } else {
                await api.post('/notes', { ...payload, authorName: currentUser?.name });
            }
            fetchNotes();
            closeModal();
        } catch (e) {
            alert('Erro ao salvar anotação.');
            console.error(e);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta anotação?')) return;
        try {
            await api.delete(`/notes/${id}`);
            fetchNotes();
        } catch (e) {
            alert('Erro ao excluir anotação.');
        }
    };

    const openEdit = (note) => {
        setEditingId(note._id);
        setTitle(note.title);
        setContent(note.content);
        setVisibility(note.visibility || 'public');
        const noteUsers = note.allowedUsers?.map(u => u._id || u) || [];
        const noteSectors = note.allowedSectors?.map(s => s._id || s) || [];
        setAllowedUsers(noteUsers);
        setAllowedSectors(noteSectors);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
        setTitle('');
        setContent('');
        setVisibility('public');
        setAllowedUsers([]);
        setAllowedSectors([]);
    };

    const canEdit = (note) => {
        if (!currentUser) return false;
        return currentUser.role === 'admin' || String(note.authorId) === String(currentUser._id || currentUser.id);
    };

    const stripHtml = (html) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const filteredNotes = notes.filter(n =>
        n.title.toLowerCase().includes(search.toLowerCase()) ||
        stripHtml(n.content).toLowerCase().includes(search.toLowerCase())
    );

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    return (
        <div className="p-6 max-w-7xl mx-auto h-full flex flex-col">
            {/* Cabeçalho */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                        <BookOpen className="w-8 h-8 mr-3 text-blue-600" />
                        Caderno de Anotações
                    </h1>
                    <p className="text-gray-500 mt-1">Registre informações, procedimentos e scripts úteis.</p>
                </div>
                
                <div className="flex w-full md:w-auto gap-3">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Buscar anotações..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                    </div>
                    <button 
                        onClick={() => setModalOpen(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-5 h-5 mr-1" /> Nova Anotação
                    </button>
                </div>
            </div>

            {/* Listagem */}
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
            ) : filteredNotes.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">Nenhuma anotação encontrada</h3>
                    <p className="text-gray-500 mt-1">Clique em "Nova Anotação" para começar a registrar.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto pb-6">
                    {filteredNotes.map(note => (
                        <div key={note._id} onClick={() => setViewingNote(note)} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-5 flex flex-col h-64 group relative overflow-hidden cursor-pointer">
                            <div className="flex justify-between items-start mb-3">
                                <h3 className="font-bold text-gray-800 text-lg line-clamp-1 flex-1 pr-2">{note.title}</h3>
                                {note.visibility === 'public' && (
                                    <span className="flex items-center text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full whitespace-nowrap">
                                        <Globe className="w-3 h-3 mr-1" /> Público
                                    </span>
                                )}
                                {note.visibility === 'private' && (
                                    <span className="flex items-center text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full whitespace-nowrap">
                                        <Lock className="w-3 h-3 mr-1" /> Privado
                                    </span>
                                )}
                                {note.visibility === 'custom' && (
                                    <span className="flex items-center text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full whitespace-nowrap">
                                        <Users className="w-3 h-3 mr-1" /> Personalizado
                                    </span>
                                )}
                            </div>
                            
                            <p className="text-gray-600 text-sm line-clamp-4 flex-1 mb-4 leading-relaxed">
                                {stripHtml(note.content) || "Sem conteúdo textual."}
                            </p>
                            
                            <div className="flex items-center justify-between text-xs text-gray-400 mt-auto pt-4 border-t border-gray-100">
                                <div className="flex items-center">
                                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mr-2">
                                        {note.authorName ? note.authorName.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <span>{note.authorName}</span>
                                </div>
                                <div className="flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {new Date(note.createdAt).toLocaleDateString()}
                                </div>
                            </div>

                            {canEdit(note) && (
                                <div className="absolute top-0 right-0 p-3 bg-gradient-to-l from-white via-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                    <button onClick={(e) => { e.stopPropagation(); openEdit(note); }} className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100" title="Editar">
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(note._id); }} className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100" title="Excluir">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-in zoom-in-95">
                        <div className="flex justify-between items-center p-5 border-b border-gray-100">
                            <h2 className="text-xl font-bold text-gray-800">{editingId ? 'Editar Anotação' : 'Nova Anotação'}</h2>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="p-5 overflow-y-auto flex-1 flex flex-col gap-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Título da Anotação</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Ex: Script de Vendas - Produto A"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Visibilidade</label>
                                <div className="flex flex-wrap gap-4">
                                    <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${visibility === 'public' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'border-gray-200 hover:bg-gray-50'}`}>
                                        <input type="radio" name="visibility" value="public" checked={visibility === 'public'} onChange={() => setVisibility('public')} className="mr-2" />
                                        <Globe className="w-4 h-4 mr-2" />
                                        <div>
                                            <div className="font-semibold text-sm">Público</div>
                                            <div className="text-xs opacity-70">Todos podem ver</div>
                                        </div>
                                    </label>
                                    <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${visibility === 'private' ? 'bg-purple-50 border-purple-200 text-purple-800' : 'border-gray-200 hover:bg-gray-50'}`}>
                                        <input type="radio" name="visibility" value="private" checked={visibility === 'private'} onChange={() => setVisibility('private')} className="mr-2" />
                                        <Lock className="w-4 h-4 mr-2" />
                                        <div>
                                            <div className="font-semibold text-sm">Privado</div>
                                            <div className="text-xs opacity-70">Somente eu e administradores</div>
                                        </div>
                                    </label>
                                    <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${visibility === 'custom' ? 'bg-indigo-50 border-indigo-200 text-indigo-800' : 'border-gray-200 hover:bg-gray-50'}`}>
                                        <input type="radio" name="visibility" value="custom" checked={visibility === 'custom'} onChange={() => setVisibility('custom')} className="mr-2" />
                                        <Users className="w-4 h-4 mr-2" />
                                        <div>
                                            <div className="font-semibold text-sm">Personalizado</div>
                                            <div className="text-xs opacity-70">Escolha setores e/ou usuários</div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {visibility === 'custom' && (
                                <div className="border border-indigo-100 rounded-lg p-4 bg-indigo-50/30">
                                    <p className="text-xs text-indigo-600 font-medium mb-3">
                                        Selecione os setores e/ou usuários que terão acesso a esta anotação. Pode combinar os dois.
                                    </p>
                                    <div className="flex flex-col md:flex-row gap-4">
                                        {/* Setores */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block shrink-0"></span>
                                                Setores
                                                {allowedSectors.length > 0 && (
                                                    <span className="ml-1 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{allowedSectors.length}</span>
                                                )}
                                            </p>
                                            <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 min-h-[80px] max-h-44 overflow-y-auto">
                                                {sectors.map(s => (
                                                    <label key={s._id || s.id} className={`flex items-center text-sm cursor-pointer p-1.5 rounded transition-colors border whitespace-nowrap ${allowedSectors.includes(s._id || s.id) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-gray-50/50 border-transparent hover:bg-blue-50/50 hover:border-blue-200'}`}>
                                                        <input 
                                                            type="checkbox" 
                                                            className="mr-2 rounded text-blue-600 focus:ring-blue-500"
                                                            checked={allowedSectors.includes(s._id || s.id)}
                                                            onChange={(e) => {
                                                                const id = s._id || s.id;
                                                                if (e.target.checked) setAllowedSectors(prev => [...prev, id]);
                                                                else setAllowedSectors(prev => prev.filter(x => x !== id));
                                                            }}
                                                        />
                                                        <span className="text-gray-800">{s.name}</span>
                                                    </label>
                                                ))}
                                                {sectors.length === 0 && <span className="text-xs text-gray-400 w-full self-center text-center">Nenhum setor cadastrado</span>}
                                            </div>
                                        </div>

                                        {/* Divisor */}
                                        <div className="hidden md:flex items-stretch">
                                            <div className="w-px bg-gray-200 mx-1"></div>
                                        </div>
                                        <div className="md:hidden h-px bg-gray-200 my-1"></div>

                                        {/* Usuários */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 inline-block shrink-0"></span>
                                                Usuários específicos
                                                {allowedUsers.length > 0 && (
                                                    <span className="ml-1 bg-indigo-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{allowedUsers.length}</span>
                                                )}
                                            </p>
                                            <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 min-h-[80px] max-h-44 overflow-y-auto">
                                                {users.map(u => (
                                                    <label key={u._id || u.id} className={`flex items-center text-sm cursor-pointer p-1.5 rounded transition-colors border whitespace-nowrap ${allowedUsers.includes(u._id || u.id) ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-gray-50/50 border-transparent hover:bg-indigo-50/50 hover:border-indigo-200'}`}>
                                                        <input 
                                                            type="checkbox" 
                                                            className="mr-2 rounded text-indigo-600 focus:ring-indigo-500"
                                                            checked={allowedUsers.includes(u._id || u.id)}
                                                            onChange={(e) => {
                                                                const id = u._id || u.id;
                                                                if (e.target.checked) setAllowedUsers(prev => [...prev, id]);
                                                                else setAllowedUsers(prev => prev.filter(x => x !== id));
                                                            }}
                                                        />
                                                        <span className="text-gray-800">{u.name}</span>
                                                    </label>
                                                ))}
                                                {users.length === 0 && <span className="text-xs text-gray-400 w-full self-center text-center">Nenhum usuário cadastrado</span>}
                                            </div>
                                        </div>
                                    </div>
                                    {(allowedSectors.length > 0 || allowedUsers.length > 0) && (
                                        <p className="text-xs text-indigo-600 mt-3 font-medium">
                                            ✓ {[
                                                allowedSectors.length > 0 ? `${allowedSectors.length} setor(es)` : '',
                                                allowedUsers.length > 0 ? `${allowedUsers.length} usuário(s)` : ''
                                            ].filter(Boolean).join(' e ')} selecionado(s)
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="flex-1 flex flex-col min-h-[300px]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Conteúdo</label>
                                <div className="flex-1 border rounded-lg overflow-hidden border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all flex flex-col">
                                    <ReactQuill 
                                        theme="snow" 
                                        value={content} 
                                        onChange={setContent} 
                                        modules={modules}
                                        className="flex-1 flex flex-col h-full bg-white quill-custom"
                                        placeholder="Escreva sua anotação aqui... Você pode colar imagens diretamente."
                                    />
                                    <style dangerouslySetInnerHTML={{__html: `
                                        .quill-custom .ql-container { font-family: inherit; font-size: 1rem; border-bottom: none; border-left: none; border-right: none; border-bottom-left-radius: 0.5rem; border-bottom-right-radius: 0.5rem; flex: 1; display: flex; flex-direction: column; overflow: hidden; min-height: 200px; }
                                        .quill-custom .ql-editor { flex: 1; overflow-y: auto; }
                                        .quill-custom .ql-toolbar { border-top: none; border-left: none; border-right: none; background: #f9fafb; border-top-left-radius: 0.5rem; border-top-right-radius: 0.5rem; border-bottom: 1px solid #e5e7eb; }
                                    `}} />
                                </div>
                            </div>
                        </div>

                        <div className="p-5 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                            <button onClick={closeModal} className="px-5 py-2 text-gray-600 font-medium hover:bg-gray-200 rounded-lg transition-colors">
                                Cancelar
                            </button>
                            <button onClick={handleSave} className="px-5 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 rounded-lg transition-colors">
                                {editingId ? 'Salvar Alterações' : 'Criar Anotação'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Visualização */}
            {viewingNote && (
                <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setViewingNote(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-start p-6 border-b border-gray-100">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800 mb-2">{viewingNote.title}</h2>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                    <div className="flex items-center">
                                        <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mr-2 text-xs">
                                            {viewingNote.authorName ? viewingNote.authorName.charAt(0).toUpperCase() : '?'}
                                        </div>
                                        <span>{viewingNote.authorName}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Clock className="w-4 h-4 mr-1" />
                                        {new Date(viewingNote.createdAt).toLocaleString()}
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setViewingNote(null)} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="p-8 overflow-y-auto flex-1 bg-gray-50/50 custom-scrollbar">
                            <div className="prose max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: viewingNote.content }} />
                        </div>

                        <div className="p-4 border-t border-gray-100 flex justify-end bg-gray-50 rounded-b-xl">
                            <button onClick={() => setViewingNote(null)} className="px-6 py-2 bg-gray-800 text-white font-medium hover:bg-gray-900 rounded-lg transition-colors">
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CadernoScreen;
